import { type AmsRef, amsRefKey } from './ams-ref';

/** A record the user picked, kept so the browser can offer it again. */
export type RecentPick = {
  ref: AmsRef;
  displayName: string;
  /** The account the record belongs to, as the browser showed it. */
  accountName?: string;
};

export type AmsBrowserRecents = {
  queries: string[];
  picks: RecentPick[];
};

const MAX_RECENTS = 10;

const storageKey = (userId: string) => `cara.amsBrowser.recents.${userId}`;

const empty = (): AmsBrowserRecents => ({ queries: [], picks: [] });

/**
 * The user's recent queries and picks on this device. Storage can be missing
 * or refuse access (private windows, blocked site data), which reads as none.
 */
export const readRecents = (userId: string | undefined): AmsBrowserRecents => {
  if (!userId) return empty();
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<Record<keyof AmsBrowserRecents, unknown>>;
    return {
      queries: listOf(parsed.queries, (q): q is string => typeof q === 'string'),
      picks: listOf(parsed.picks, isRecentPick)
    };
  } catch {
    return empty();
  }
};

/** Stored entries of the expected shape, so a stale or edited store reads as fewer recents. */
const listOf = <T>(value: unknown, valid: (item: unknown) => item is T): T[] =>
  Array.isArray(value) ? value.filter(valid).slice(0, MAX_RECENTS) : [];

const isRecentPick = (item: unknown): item is RecentPick => {
  const pick = item as Partial<RecentPick> | null;
  return (
    typeof pick?.displayName === 'string' &&
    typeof pick.ref?.integrationId === 'string' &&
    typeof pick.ref.kind === 'string' &&
    typeof pick.ref.externalId === 'string'
  );
};

/** Puts the confirmed queries and picks first, newest first, deduplicated. */
export const withRecents = (
  recents: AmsBrowserRecents,
  queries: string[],
  picks: RecentPick[]
): AmsBrowserRecents => {
  const nextQueries = [...queries.map((q) => q.trim()).filter(Boolean), ...recents.queries];
  const nextPicks = [...picks, ...recents.picks];
  const seenQueries = new Set<string>();
  const seenPicks = new Set<string>();
  return {
    queries: nextQueries
      .filter((q) => {
        const key = q.toLowerCase();
        if (seenQueries.has(key)) return false;
        seenQueries.add(key);
        return true;
      })
      .slice(0, MAX_RECENTS),
    picks: nextPicks
      .filter((pick) => {
        const key = amsRefKey(pick.ref);
        if (seenPicks.has(key)) return false;
        seenPicks.add(key);
        return true;
      })
      .slice(0, MAX_RECENTS)
  };
};

export const writeRecents = (userId: string | undefined, recents: AmsBrowserRecents) => {
  if (!userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(recents));
  } catch {
    // A device that will not store recents simply shows none.
  }
};
